import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { checkWebAddressCountries } from './company.repo';

/**
 * Server-side redirect check for marketplace companies.
 * Returns a Next.js redirect object if the company should redirect to the marketplace,
 * or null if no redirect is needed.
 *
 * The destination preserves the original path (minus the country prefix already
 * included in redirect_url) and the query string, so deep links survive the redirect.
 *
 * Example:
 *   In:  https://foo.site.agendaprodev.com/cl/sign_consents/my_consent?code=abc
 *   Out: https://www.agendaprodev.com/site/cl/foo/sign_consents/my_consent?code=abc
 */
export const getMarketplaceRedirect = async (
  ctx: GetServerSidePropsContext,
): Promise<{ redirect: { permanent: boolean; destination: string } } | null> => {
  const companySlug = ctx.req.headers.host?.split('.')[0];

  if (!companySlug) {
    return null;
  }

  try {
    const { redirect } = await checkWebAddressCountries(companySlug);

    if (!redirect?.needsRedirect || !redirect?.redirectUrl) {
      return null;
    }

    const base = redirect.redirectUrl.replace(/\/$/, '');
    // E-commerce routes start with /[countryCode]/..., and the e-commerce uses
    // /sucursal/<id> while the marketplace uses /<id> directly. We strip both
    // segments so the deep link resolves to the correct marketplace URL.
    const rawPath = (ctx.req.url ?? '').replace(/^\/[^/?]+(\/sucursal)?/, '');
    const pathWithoutCountry = rawPath === '/' ? '' : rawPath;

    return {
      redirect: {
        permanent: false, // 302: site_type can change if the company upgrades/downgrades plan
        destination: `${base}${pathWithoutCountry}`,
      },
    };
  } catch {
    return null;
  }
};

/**
 * Wraps a getServerSideProps handler so the marketplace redirect check runs first.
 * Centralizes the redirect-before-fetch pattern shared by every entry page.
 */
export const withMarketplaceRedirect =
  <P extends { [key: string]: any }>(handler: GetServerSideProps<P>): GetServerSideProps<P> =>
  async (ctx) => {
    const marketplaceRedirect = await getMarketplaceRedirect(ctx);

    if (marketplaceRedirect) {
      return marketplaceRedirect;
    }
    return handler(ctx);
  };
